import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { BookOpen, ArrowLeft, Heart, Bell, Target, Plus, X, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const ReadingPreferencesPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newAuthor, setNewAuthor] = useState('');
  
  const [preferences, setPreferences] = useState({
    favorite_categories: [],
    favorite_authors: [],
    notify_new_arrivals: true,
    notify_categories: [],
    reading_goal_monthly: 2,
    preferred_language: 'English'
  });

  const languages = ['English', 'Hindi', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Malayalam', 'Sanskrit', 'Others'];

  useEffect(() => {
    fetchCategories();
    fetchPreferences();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/auth/me/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences({
        favorite_categories: response.data.favorite_categories || [],
        favorite_authors: response.data.favorite_authors || [],
        notify_new_arrivals: response.data.notify_new_arrivals ?? true,
        notify_categories: response.data.notify_categories || [],
        reading_goal_monthly: response.data.reading_goal_monthly || 2,
        preferred_language: response.data.preferred_language || 'English'
      });
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setPreferences(prev => ({
      ...prev,
      favorite_categories: prev.favorite_categories.includes(categoryName)
        ? prev.favorite_categories.filter(c => c !== categoryName)
        : [...prev.favorite_categories, categoryName]
    }));
  };

  const toggleNotifyCategory = (categoryName) => {
    setPreferences(prev => ({
      ...prev,
      notify_categories: prev.notify_categories.includes(categoryName)
        ? prev.notify_categories.filter(c => c !== categoryName)
        : [...prev.notify_categories, categoryName]
    }));
  };

  const addAuthor = () => {
    if (newAuthor.trim() && !preferences.favorite_authors.includes(newAuthor.trim())) {
      setPreferences(prev => ({
        ...prev,
        favorite_authors: [...prev.favorite_authors, newAuthor.trim()]
      }));
      setNewAuthor('');
    }
  };

  const removeAuthor = (author) => {
    setPreferences(prev => ({
      ...prev,
      favorite_authors: prev.favorite_authors.filter(a => a !== author)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved successfully!');
    } catch (error) {
      toast.error('Failed to save preferences');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl">Loading preferences...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <BookOpen className="h-8 w-8" style={{color: 'var(--primary)'}} />
              <span className="text-2xl font-bold" style={{color: 'var(--primary)'}}>RentMyBook</span>
            </div>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{color: 'var(--primary)'}}>
            <Sparkles className="inline h-8 w-8 mr-2" />
            Reading Preferences
          </h1>
          <p className="text-gray-600 mt-2">
            Tell us what you love to read, and we'll personalize your experience!
          </p>
        </div>

        <div className="space-y-8">
          {/* Favorite Categories */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Heart className="h-6 w-6 mr-2" style={{color: 'var(--accent)'}} />
              <h2 className="text-xl font-semibold" style={{color: 'var(--foreground)'}}>
                Favorite Genres
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select the genres you enjoy reading the most
            </p>
            <div className="flex flex-wrap gap-3">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.name)}
                  className={`px-4 py-2 rounded-full font-medium transition-all flex items-center space-x-2 ${
                    preferences.favorite_categories.includes(category.name)
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={preferences.favorite_categories.includes(category.name) 
                    ? {backgroundColor: 'var(--primary)'} 
                    : {}}
                  data-testid={`category-${category.name}`}
                >
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                  {preferences.favorite_categories.includes(category.name) && (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Favorite Authors */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <BookOpen className="h-6 w-6 mr-2" style={{color: 'var(--accent)'}} />
              <h2 className="text-xl font-semibold" style={{color: 'var(--foreground)'}}>
                Favorite Authors
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Add your favorite authors to get notified when their books are available
            </p>
            
            <div className="flex space-x-2 mb-4">
              <Input
                value={newAuthor}
                onChange={(e) => setNewAuthor(e.target.value)}
                placeholder="Enter author name"
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && addAuthor()}
                data-testid="author-input"
              />
              <Button onClick={addAuthor} style={{backgroundColor: 'var(--primary)', color: 'white'}}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {preferences.favorite_authors.map((author, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100"
                  style={{color: 'var(--primary)'}}
                >
                  {author}
                  <button
                    onClick={() => removeAuthor(author)}
                    className="ml-2 hover:text-red-500"
                    data-testid={`remove-author-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
              {preferences.favorite_authors.length === 0 && (
                <p className="text-gray-400 text-sm italic">No authors added yet</p>
              )}
            </div>
          </div>

          {/* Preferred Language */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-2">🌐</span>
              <h2 className="text-xl font-semibold" style={{color: 'var(--foreground)'}}>
                Preferred Language
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select your preferred reading language
            </p>
            <div className="flex flex-wrap gap-2">
              {languages.map(lang => (
                <button
                  key={lang}
                  onClick={() => setPreferences(prev => ({...prev, preferred_language: lang}))}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    preferences.preferred_language === lang
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={preferences.preferred_language === lang 
                    ? {backgroundColor: 'var(--accent)'} 
                    : {}}
                  data-testid={`lang-${lang}`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          {/* Reading Goal */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Target className="h-6 w-6 mr-2" style={{color: 'var(--accent)'}} />
              <h2 className="text-xl font-semibold" style={{color: 'var(--foreground)'}}>
                Monthly Reading Goal
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Set a target for how many books you'd like to read each month
            </p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map(num => (
                  <button
                    key={num}
                    onClick={() => setPreferences(prev => ({...prev, reading_goal_monthly: num}))}
                    className={`w-12 h-12 rounded-full font-bold text-lg transition-all ${
                      preferences.reading_goal_monthly === num
                        ? 'text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={preferences.reading_goal_monthly === num 
                      ? {backgroundColor: 'var(--primary)'} 
                      : {}}
                    data-testid={`goal-${num}`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => setPreferences(prev => ({...prev, reading_goal_monthly: 6}))}
                  className={`px-4 h-12 rounded-full font-bold transition-all ${
                    preferences.reading_goal_monthly >= 6
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={preferences.reading_goal_monthly >= 6 
                    ? {backgroundColor: 'var(--primary)'} 
                    : {}}
                >
                  6+
                </button>
              </div>
              <span className="text-gray-600">books/month</span>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Bell className="h-6 w-6 mr-2" style={{color: 'var(--accent)'}} />
              <h2 className="text-xl font-semibold" style={{color: 'var(--foreground)'}}>
                Notification Preferences
              </h2>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <Label className="text-base font-medium">New Book Arrivals</Label>
                  <p className="text-sm text-gray-500">Get notified when new books matching your interests are added</p>
                </div>
                <Switch
                  checked={preferences.notify_new_arrivals}
                  onCheckedChange={(checked) => setPreferences(prev => ({...prev, notify_new_arrivals: checked}))}
                  data-testid="notify-switch"
                />
              </div>

              {preferences.notify_new_arrivals && (
                <div>
                  <Label className="text-sm font-medium text-gray-600 mb-3 block">
                    Notify me for these categories:
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => toggleNotifyCategory(category.name)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center space-x-1 ${
                          preferences.notify_categories.includes(category.name)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`notify-cat-${category.name}`}
                      >
                        <Bell className="h-3 w-3" />
                        <span>{category.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {preferences.notify_categories.length === 0 
                      ? 'Select categories to receive notifications'
                      : `You'll be notified for ${preferences.notify_categories.length} categor${preferences.notify_categories.length === 1 ? 'y' : 'ies'}`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="px-8"
              style={{backgroundColor: 'var(--primary)', color: 'white'}}
              data-testid="save-preferences-btn"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReadingPreferencesPage;
